import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Stack,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TablePagination,
    CircularProgress,
    Alert,
    Avatar,
    Skeleton
} from '@mui/material';
import {
    Search,
    FilterList,
    Security,
    Person,
    Event,
    Computer,
    Refresh
} from '@mui/icons-material';
import { getAdminLogs } from '../../../api/admin';

const AdminLogsTable = ({ compact = false, maxRows = null }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(maxRows || 10);
    const [totalElements, setTotalElements] = useState(0);

    // Filter states
    const [filters, setFilters] = useState({
        action: '',
        userId: '',
        startDate: '',
        endDate: '',
        ipAddress: ''
    });
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchLogs();
    }, [page, size]);

    const fetchLogs = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await getAdminLogs();

            if (response.data) {
                let logsData = [];

                if (Array.isArray(response.data)) {
                    logsData = response.data;
                } else if (response.data.content && Array.isArray(response.data.content)) {
                    logsData = response.data.content;
                    setTotalElements(response.data.totalElements || response.data.content.length);
                } else {
                    logsData = [];
                }

                // Transform the real backend data to match expected format
                const transformedLogs = logsData.map(log => ({
                    id: log.id,
                    timestamp: log.actionTime, // Backend sends actionTime
                    action: log.actionType,    // Backend sends actionType
                    performedBy: {
                        id: log.actorEmail ? log.actorEmail.split('@')[0] : 'Unknown', // Extract name from email
                        displayName: log.actorEmail || 'Anonymous',
                        email: log.actorEmail
                    },
                    targetEntity: log.targetEntity,
                    targetId: log.targetId,
                    ipAddress: log.ipAddress,
                    userAgent: log.userAgent,
                    details: log.metaData || 'No details available',
                    role: log.role
                }));

                // Apply local filters
                let filteredLogs = transformedLogs;

                // Search filter
                if (searchTerm) {
                    filteredLogs = filteredLogs.filter(log =>
                        log.details?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        log.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        log.performedBy?.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
                    );
                }

                // Action filter
                if (filters.action) {
                    filteredLogs = filteredLogs.filter(log => log.action === filters.action);
                }

                // User ID filter
                if (filters.userId) {
                    filteredLogs = filteredLogs.filter(log =>
                        log.performedBy?.displayName?.toLowerCase().includes(filters.userId.toLowerCase()) ||
                        log.performedBy?.email?.toLowerCase().includes(filters.userId.toLowerCase())
                    );
                }

                // Date range filter
                if (filters.startDate) {
                    const startDate = new Date(filters.startDate);
                    filteredLogs = filteredLogs.filter(log => {
                        const logDate = new Date(log.timestamp);
                        return logDate >= startDate;
                    });
                }

                if (filters.endDate) {
                    const endDate = new Date(filters.endDate);
                    filteredLogs = filteredLogs.filter(log => {
                        const logDate = new Date(log.timestamp);
                        return logDate <= endDate;
                    });
                }

                // IP Address filter
                if (filters.ipAddress) {
                    filteredLogs = filteredLogs.filter(log =>
                        log.ipAddress?.includes(filters.ipAddress)
                    );
                }

                setLogs(filteredLogs);
                setTotalElements(filteredLogs.length);
            } else {
                setLogs([]);
                setTotalElements(0);
            }
        } catch (error) {
            console.error('Failed to fetch logs:', error);
            setError('Failed to load system logs from server');

            // Set empty array on error, no mock data
            setLogs([]);
            setTotalElements(0);
        } finally {
            setLoading(false);
        }
    };

    const handleFilter = () => {
        setPage(0);
        fetchLogs();
    };

    const clearFilters = () => {
        setFilters({
            action: '',
            userId: '',
            startDate: '',
            endDate: '',
            ipAddress: ''
        });
        setSearchTerm('');
        setPage(0);
        setTimeout(() => fetchLogs(), 100);
    };

    const formatTimestamp = (timestamp) => {
        try {
            const date = new Date(timestamp);
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            return date.toLocaleString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch (error) {
            return 'Invalid Date';
        }
    };

    const getActionColor = (action) => {
        switch (action?.toLowerCase()) {
            case 'create': return '#4caf50';
            case 'update': return '#2196f3';
            case 'delete': return '#f44336';
            case 'login': return '#9c27b0';
            case 'logout': return '#ff9800';
            case 'classify': return '#4caf50';
            case 'status_change': return '#607d8b';
            case 'role_change': return '#795548';
            default: return '#757575';
        }
    };

    const getActionIcon = (action) => {
        switch (action?.toLowerCase()) {
            case 'create': case 'update': case 'delete': return <Security fontSize="small" />;
            case 'login': case 'logout': return <Person fontSize="small" />;
            case 'classify': return <Event fontSize="small" />;
            default: return <Computer fontSize="small" />;
        }
    };

    const getUserDisplayName = (performedBy) => {
        if (!performedBy || !performedBy.displayName) {
            return 'Anonymous';
        }

        // If it's an email, extract the username part
        if (performedBy.displayName.includes('@')) {
            return performedBy.displayName.split('@')[0];
        }

        return performedBy.displayName;
    };

    if (loading) {
        return (
            <Card elevation={compact ? 1 : 4} sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: compact ? 2 : 3 }}>
                    <Stack spacing={2}>
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} variant="rectangular" height={60} />
                        ))}
                    </Stack>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card elevation={compact ? 1 : 4} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: compact ? 2 : 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant={compact ? "h6" : "h5"} fontWeight={700} sx={{ color: '#1b5e20' }}>
                        🔒 {compact ? 'Recent Logs' : 'System Activity Logs'}
                    </Typography>
                    <Button
                        size="small"
                        startIcon={<Refresh />}
                        onClick={fetchLogs}
                        sx={{ minWidth: 'auto' }}
                    >
                        Refresh
                    </Button>
                </Box>

                {/* Filters Section - Only show in full view */}
                {!compact && (
                    <Card elevation={1} sx={{ mb: 3, backgroundColor: '#f8f9fa' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                <FilterList sx={{ color: '#666' }} />
                                <Typography variant="h6" color="text.secondary">
                                    Filter Logs
                                </Typography>
                            </Stack>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                                <TextField
                                    size="small"
                                    label="Search"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search logs..."
                                    sx={{ minWidth: 200 }}
                                />
                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                    <InputLabel>Action</InputLabel>
                                    <Select
                                        value={filters.action}
                                        label="Action"
                                        onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                                    >
                                        <MenuItem value="">All Actions</MenuItem>
                                        <MenuItem value="CREATE">Create</MenuItem>
                                        <MenuItem value="UPDATE">Update</MenuItem>
                                        <MenuItem value="DELETE">Delete</MenuItem>
                                        <MenuItem value="LOGIN">Login</MenuItem>
                                        <MenuItem value="LOGOUT">Logout</MenuItem>
                                        <MenuItem value="CLASSIFY">Classification</MenuItem>
                                        <MenuItem value="STATUS_CHANGE">Status Change</MenuItem>
                                        <MenuItem value="ROLE_CHANGE">Role Change</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    size="small"
                                    label="User"
                                    value={filters.userId}
                                    onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
                                    placeholder="User email or name"
                                    sx={{ minWidth: 150 }}
                                />
                                <TextField
                                    size="small"
                                    label="Start Date"
                                    type="datetime-local"
                                    value={filters.startDate}
                                    onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    size="small"
                                    label="End Date"
                                    type="datetime-local"
                                    value={filters.endDate}
                                    onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Stack>
                            <Stack direction="row" spacing={2}>
                                <Button
                                    variant="contained"
                                    startIcon={<Search />}
                                    onClick={handleFilter}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    Apply Filters
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={clearFilters}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    Clear All
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>
                )}

                {error && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <TableContainer component={Paper} elevation={0} sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    maxHeight: compact ? 400 : 'none'
                }}>
                    <Table size="small">
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                                {!compact && <TableCell sx={{ fontWeight: 700 }}>Target</TableCell>}
                                {!compact && <TableCell sx={{ fontWeight: 700 }}>IP</TableCell>}
                                <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={compact ? 4 : 6} sx={{ textAlign: 'center', py: 4 }}>
                                        <Security sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                                        <Typography variant="h6" color="text.secondary">
                                            No system logs found
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {error ? 'Unable to connect to server' : 'No log entries match your filters'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                logs.slice(page * size, (page + 1) * size).map((log) => (
                                    <TableRow key={log.id} hover>
                                        <TableCell>
                                            <Typography variant="caption" fontWeight={600}>
                                                {formatTimestamp(log.timestamp)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={getActionIcon(log.action)}
                                                label={log.action || 'Unknown'}
                                                size="small"
                                                sx={{
                                                    backgroundColor: `${getActionColor(log.action)}15`,
                                                    color: getActionColor(log.action),
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Avatar sx={{ width: 20, height: 20, fontSize: '0.7rem' }}>
                                                    {getUserDisplayName(log.performedBy).charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Typography variant="caption" fontWeight={600}>
                                                    {getUserDisplayName(log.performedBy)}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        {!compact && (
                                            <TableCell>
                                                <Typography variant="caption">
                                                    {log.targetEntity || 'N/A'}
                                                    {log.targetId && ` #${log.targetId}`}
                                                </Typography>
                                            </TableCell>
                                        )}
                                        {!compact && (
                                            <TableCell>
                                                <Typography variant="caption">
                                                    {log.ipAddress || 'N/A'}
                                                </Typography>
                                            </TableCell>
                                        )}
                                        <TableCell sx={{ maxWidth: compact ? 200 : 200 }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: compact ? 1 : 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}
                                                title={log.details}
                                            >
                                                {log.details || 'No details'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {!compact && logs.length > 0 && (
                    <TablePagination
                        component="div"
                        count={totalElements}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        rowsPerPage={size}
                        onRowsPerPageChange={(event) => {
                            setSize(parseInt(event.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                )}
            </CardContent>
        </Card>
    );
};

export default AdminLogsTable;
